import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { components } from "@/lib/openapi";
import { getLocations } from "@/queries/playlists";
import { getTeams } from "@/queries/team";
import { useSessionData } from "@/stores/SessionDataContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { GripVertical, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { DialogFooter } from "@/components/ui/dialog";

// @dnd-kit imports
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type Rule = components["schemas"]["RuleSchema"];

const actionSchema = z.object({
  title: z.string().min(1, "Action title is required"),
  team_id: z.number().nullable(),
  order: z.number().default(0),
});

const formSchema = z.object({
  title: z.string().min(1).max(255),
  category: z.string().min(1).max(255),
  location_type: z.string().min(1, "Location type is required"),
  condition_type: z.string().min(1),
  terminal: z.string().nullable(),
  concourse: z.string().nullable(),
  location_filter: z.string().nullable(),
  threshold: z.string().min(1),
  actions: z.array(actionSchema),
  enabled: z.boolean(),
});

type FormValues = {
  title: string;
  category: string;
  location_type: string;
  condition_type: string;
  terminal: string | null;
  concourse: string | null;
  location_filter: string | null;
  threshold: string;
  actions: Array<{
    title: string;
    team_id: number | null;
    order: number;
  }>;
  enabled: boolean;
};

// Separate ActionItem component
function ActionItem({
  id,
  index,
  onRemove,
  form,
  teamsList,
}: {
  id: string;
  index: number;
  onRemove: () => void;
  form: any;
  teamsList: any[];
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`space-y-2 p-4 border rounded-lg transition-all duration-200 ${
        isDragging ? "opacity-50 scale-95 z-50" : "opacity-100 scale-100"
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div
            className="cursor-grab active:cursor-grabbing touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
          <h4 className="text-sm font-medium">Action {index + 1}</h4>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <FormField
        control={form.control}
        name={`actions.${index}.title`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Action Description</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                placeholder={`Describe what should be done for action ${index + 1}`}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`actions.${index}.team_id`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Assign to Team (Optional)</FormLabel>
            <Select
              onValueChange={(value) =>
                field.onChange(value ? parseInt(value) : null)
              }
              value={field.value?.toString() || ""}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select team (optional)" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {teamsList.map((team) => (
                  <SelectItem key={team.id} value={team.id.toString()}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

export function RuleForm({
  mode,
  rule,
  onSuccess,
  mutation,
}: {
  mode: "create" | "edit";
  rule?: Rule;
  onSuccess: () => void;
  mutation: any;
}) {
  const sessionData = useSessionData();
  const { airport_config, airport } = sessionData;

  // Fetch teams for dropdown
  const { data: teams } = getTeams();
  const teamsList = teams || [];

  // Extract terminals from session data
  const terminalsList = useMemo(() => {
    return airport?.terminal || [];
  }, [airport]);

  // Extract all concourses from all terminals
  const concoursesList = useMemo(() => {
    const concourses: Array<{
      id: number;
      name: string;
      terminalName: string;
    }> = [];
    terminalsList.forEach((terminal) => {
      terminal.concourses.forEach((concourse) => {
        concourses.push({
          id: concourse.id,
          name: concourse.name,
          terminalName: terminal.name,
        });
      });
    });
    return concourses;
  }, [terminalsList]);

  // @dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const makeDefaultValues = (rule?: Rule): FormValues => {
    // Convert rule_actions to the new format, or create default action
    const actions = rule?.rule_actions?.length
      ? rule.rule_actions.map((action, index) => ({
          title: action.title,
          team_id: action.team?.id || null,
          order: action.order || index,
        }))
      : [{ title: "", team_id: null, order: 0 }];

    return {
      title: rule?.title ?? "",
      category: rule?.category ?? "General",
      location_type: rule?.location_type ?? "gate",
      condition_type: rule?.condition_type ?? "above_threshold",
      terminal: rule?.terminal ?? "",
      concourse: rule?.concourse ?? "",
      location_filter: rule?.location_filter ?? "",
      threshold: rule?.threshold?.toString() ?? "200",
      actions,
      enabled: rule?.enabled ?? true,
    };
  };

  const form = useForm<FormValues>({
    defaultValues: makeDefaultValues(rule),
    resolver: zodResolver(formSchema),
  });

  const currentLocationType = form.watch("location_type");

  // Fetch available locations based on selected location type
  const { data: locationsData } = getLocations(
    currentLocationType ? [currentLocationType] : []
  );
  const availableLocations =
    locationsData?.locations?.[currentLocationType] || [];

  // Multi-select state for location_filter
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  // Parse space-separated location_filter into array on load
  useEffect(() => {
    const locationFilter = form.getValues("location_filter");
    if (locationFilter) {
      const locations = locationFilter
        .split(" ")
        .map((loc) => loc.trim())
        .filter(Boolean);
      setSelectedLocations(locations);
    } else {
      setSelectedLocations([]);
    }
  }, [rule]);

  // Update form value when selectedLocations changes
  useEffect(() => {
    const value =
      selectedLocations.length > 0 ? selectedLocations.join(" ") : "";
    form.setValue("location_filter", value);
  }, [selectedLocations, form]);

  // Clear selected locations when location type changes
  useEffect(() => {
    setSelectedLocations([]);
    form.setValue("location_filter", "");
  }, [currentLocationType, form]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "actions",
  });

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Update order values after reordering
        const currentActions = form.getValues("actions");
        const reorderedActions = arrayMove(currentActions, oldIndex, newIndex);
        const actionsWithUpdatedOrder = reorderedActions.map(
          (action, index) => ({
            ...action,
            order: index,
          })
        );
        form.setValue("actions", actionsWithUpdatedOrder);
      }
    }
  };

  const locationTypeOptions = [
    { value: "gate", label: "Gate Lounge" },
    { value: "ticketing", label: "Ticketing" },
    { value: "checkpoint", label: "Checkpoint" },
    { value: "security_lane", label: "Security Lane" },
    { value: "restroom", label: "Restroom" },
    { value: "customs", label: "Customs" },
    { value: "intl_baggage", label: "International Baggage" },
    { value: "baggage_carousel", label: "Baggage Carousel" },
    { value: "door", label: "Door" },
    { value: "terminal", label: "Terminal" },
  ];

  const conditionTypeOptions = [
    { value: "above_threshold", label: "Above Threshold" },
    { value: "below_threshold", label: "Below Threshold" },
  ];

  const onSubmit = (values: FormValues) => {
    const mutationData = {
      ...values,
      threshold: parseInt(values.threshold, 10),
      actions: values.actions
        .filter((action) => action.title.trim() !== "")
        .map((action, index) => ({
          title: action.title,
          team_id: action.team_id,
          order: index, // Ensure order is sequential
        })),
      message: "",
    };

    mutation.mutate(mutationData, {
      onSuccess,
    });
  };

  useEffect(() => {
    const defaultValues = makeDefaultValues(rule);
    form.reset(defaultValues, {
      keepDirty: false,
      keepTouched: false,
    });

    // Update selectedLocations from rule data
    if (defaultValues.location_filter) {
      const locations = defaultValues.location_filter
        .split(" ")
        .map((loc) => loc.trim())
        .filter(Boolean);
      setSelectedLocations(locations);
    } else {
      setSelectedLocations([]);
    }
  }, [rule, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Play Title</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Play Category</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location Type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {locationTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="condition_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Condition Type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select condition type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {conditionTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="terminal"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terminal (Optional)</FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select terminal (optional)" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {terminalsList.map((terminal) => (
                    <SelectItem key={terminal.id} value={terminal.name}>
                      {terminal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {airport_config.has_concourse && (
          <FormField
            control={form.control}
            name="concourse"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Concourse (Optional)</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value || ""}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select concourse (optional)" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {concoursesList.map((concourse) => (
                      <SelectItem key={concourse.id} value={concourse.name}>
                        {concourse.name} ({concourse.terminalName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="location_filter"
          render={() => (
            <FormItem>
              <FormLabel>Location Filter (Optional)</FormLabel>
              <div className="space-y-2">
                {/* Display selected locations as badges */}
                {selectedLocations.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[40px]">
                    {selectedLocations.map((location) => (
                      <Badge
                        key={location}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {location}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedLocations((prev) =>
                              prev.filter((loc) => loc !== location)
                            );
                          }}
                          className="ml-1 hover:bg-muted rounded-full"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {/* Dropdown to add new locations */}
                <Select
                  value=""
                  onValueChange={(value) => {
                    if (value && !selectedLocations.includes(value)) {
                      setSelectedLocations((prev) => [...prev, value]);
                    }
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Add location..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Array.isArray(availableLocations) &&
                      availableLocations
                        .filter(
                          (location: any) =>
                            !selectedLocations.includes(location.name)
                        )
                        .map((location: any) => (
                          <SelectItem key={location.id} value={location.name}>
                            {location.name}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="threshold"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Threshold</FormLabel>
              <FormControl>
                <Input {...field} type="text" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-4">
          <FormLabel>Execute Actions</FormLabel>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={fields.map((field) => field.id)}
              strategy={verticalListSortingStrategy}
            >
              {fields.map((field, index) => (
                <ActionItem
                  key={field.id}
                  id={field.id}
                  index={index}
                  onRemove={() => remove(index)}
                  form={form}
                  teamsList={teamsList}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              append({ title: "", team_id: null, order: fields.length })
            }
          >
            Add Action
          </Button>
        </div>

        <FormField
          control={form.control}
          name="enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Enabled</FormLabel>
                <FormDescription>Enable or disable this play</FormDescription>
              </div>
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? mode === "create"
                ? "Creating..."
                : "Updating..."
              : mode === "create"
                ? "Create Rule"
                : "Update Rule"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
